import React from "react";
import { Box } from "@mui/material";
import { Select } from "@outpave/ds";
import type { SelectOption } from "@outpave/ds";
import { useQuery } from "@apollo/client";
import { GET_ALLOCATION_TYPES } from "../../graphql/allocation";
import type { AllocationType } from "utils/allocations/allocationOptions";
import { useTranslation } from "react-i18next";

export interface AllocationTypeOption extends AllocationType {
  integrationEntity?: string | null;
  isLinkedCustomField?: string | null;
}

/** The allocation-type node, limited to the fields this picker reads. */
interface AllocationTypeNode {
  key: string;
  name: string;
  integrationEntity?: string | null;
  isLinkedCustomField?: string | null;
}

interface AllocationTypeDropdownProps {
  value?: AllocationType | null;
  // When the caller only has the key (e.g. stored in formik), pass it here and
  // the dropdown will resolve the full AllocationType from its own query.
  // Convenient for edit flows where the parent doesn't want to re-fetch types.
  valueKey?: string;
  onChange: (type: AllocationTypeOption | null) => void;
  /** Widened from the input element because it now rides a wrapper (the DS
   *  Select takes no onBlur), following AllocationSearchAutoComplete. */
  onBlur?: React.FocusEventHandler<HTMLElement>;
  label?: string;
  helperText?: string;
  error?: boolean;
  required?: boolean;
  disabled?: boolean;
  isEditable?: boolean;
  showAll?: boolean;
}

const AllocationTypeDropdown: React.FC<AllocationTypeDropdownProps> = ({
  value,
  valueKey,
  onChange,
  onBlur = () => {},
  label = "Allocation Type",
  helperText,
  error = false,
  required = false,
  disabled = false,
  isEditable,
  showAll = false,
}) => {
  const { t } = useTranslation();
  const { data, loading } = useQuery(GET_ALLOCATION_TYPES, {
    fetchPolicy: "network-only",
    variables: { isEditable, showAll },
    onError: (queryError) =>
      console.error("Error loading allocation types:", queryError.message),
  });

  const types: AllocationTypeOption[] = (
    (data?.allocationTypes ?? []) as AllocationTypeNode[]
  ).map((node) => ({
    key: node.key,
    name: node.name,
    integrationEntity: node.integrationEntity,
    isLinkedCustomField: node.isLinkedCustomField,
  }));

  // A type's name is tenant data and must not be translated: i18next reads ":"
  // as a namespace separator and would render "Cost: West" as " West"
  // (client/AGENTS.md). The label is a UI literal, so it does translate.
  const options: SelectOption[] = types.map((type) => ({
    id: type.key,
    label: type.name,
  }));

  const resolved: AllocationTypeOption | null =
    value && value.key !== "" && value.name !== ""
      ? value
      : valueKey
        ? (types.find((type) => type.key === valueKey) ?? null)
        : null;

  return (
    // The DS Select takes no `onBlur`; blur bubbles, so the wrapper carries it.
    <Box onBlur={onBlur}>
      <Select
        options={options}
        value={resolved?.key ?? null}
        onChange={(id) =>
          onChange(id ? (types.find((type) => type.key === id) ?? null) : null)
        }
        label={t(label)}
        // Already display text: `handleFormikHelperText` translates before it
        // returns, so a second `t()` here would re-key a rendered message.
        helperText={error ? undefined : helperText}
        errorText={error ? helperText : undefined}
        required={required}
        disabled={disabled}
        loading={loading}
      />
    </Box>
  );
};

export default AllocationTypeDropdown;
